import { bindable, customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import template from './batch-rule-dialog.html';
import type { Document, Rule, RuleApplicationResponse } from '../services/api.types';
import { applyRulesToDocument, listRules } from '../services/api.service';

type BatchRuleResult = {
  document: Document;
  response: RuleApplicationResponse | null;
  error: string | null;
};

@customElement({ name: 'batch-rule-dialog', template })
export class BatchRuleDialog {
  private readonly i18n: I18N = resolve(I18N);

  @bindable public documents: Document[] = [];
  @bindable public closed!: () => void;
  @bindable public completed?: (changed: boolean) => void;

  public rules: Rule[] = [];
  public selectedRuleId = '';
  public loadingRules = true;
  public running = false;
  public finished = false;
  public error = '';
  public processed = 0;
  public results: BatchRuleResult[] = [];

  private _keyHandler!: (event: KeyboardEvent) => void;

  public binding(): void {
    this.rules = [];
    this.selectedRuleId = '';
    this.loadingRules = true;
    this.running = false;
    this.finished = false;
    this.error = '';
    this.processed = 0;
    this.results = [];
  }

  public attached(): void {
    this._keyHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !this.running) this.close();
    };
    document.addEventListener('keydown', this._keyHandler);
    void this.loadRules();
  }

  public detached(): void {
    document.removeEventListener('keydown', this._keyHandler);
  }

  public get selectedRule(): Rule | null {
    return this.rules.find(rule => rule.id === this.selectedRuleId) ?? null;
  }

  public get canStart(): boolean {
    return !this.loadingRules &&
      !this.running &&
      !this.finished &&
      this.documents.length > 0 &&
      this.selectedRule !== null;
  }

  public get progressPercent(): number {
    if (this.documents.length === 0) return 0;
    return Math.round((this.processed / this.documents.length) * 100);
  }

  public get changedCount(): number {
    return this.results.filter(result => result.response?.changed === true).length;
  }

  public get matchedWithoutChangesCount(): number {
    return this.results.filter(result =>
      result.error === null &&
      !result.response?.error &&
      result.response?.matched === true &&
      result.response?.changed !== true).length;
  }

  public get noMatchCount(): number {
    return this.results.filter(result =>
      result.error === null &&
      !result.response?.error &&
      result.response?.matched !== true).length;
  }

  public get errorCount(): number {
    return this.results.filter(result => result.error !== null || !!result.response?.error).length;
  }

  public get summaryText(): string {
    return this.i18n.tr('documents.applyRule.summary', {
      changed: this.changedCount,
      matchedNoChanges: this.matchedWithoutChangesCount,
      unmatched: this.noMatchCount,
      errors: this.errorCount,
    });
  }

  public resultTitle(result: BatchRuleResult): string {
    const currentTitle = result.document.title;
    const newTitle = result.response?.documentTitle;
    if (newTitle && newTitle !== currentTitle) {
      return `${currentTitle} → ${newTitle}`;
    }
    return currentTitle;
  }

  public resultStatus(result: BatchRuleResult): string {
    if (result.error !== null || result.response?.error) {
      return this.i18n.tr('documents.applyRule.statusError');
    }
    if (result.response?.changed) {
      return this.i18n.tr('documents.applyRule.statusChanged');
    }
    if (result.response?.matched) {
      return this.i18n.tr('documents.applyRule.statusMatchedNoChanges');
    }
    return this.i18n.tr('documents.applyRule.statusNoMatch');
  }

  public resultStatusClass(result: BatchRuleResult): string {
    if (result.error !== null || result.response?.error) return 'error';
    if (result.response?.changed) return 'changed';
    if (result.response?.matched) return 'matched';
    return 'no-match';
  }

  public resultError(result: BatchRuleResult): string {
    return result.error ?? result.response?.error ?? '';
  }

  private async loadRules(): Promise<void> {
    try {
      this.rules = await listRules();
      if (this.rules.length === 1) this.selectedRuleId = this.rules[0].id;
    } catch (err: unknown) {
      this.error = err instanceof Error
        ? err.message
        : this.i18n.tr('documents.applyRule.loadFailed');
    } finally {
      this.loadingRules = false;
    }
  }

  public async start(): Promise<void> {
    if (!this.canStart || !this.selectedRule) return;

    this.running = true;
    this.error = '';
    this.processed = 0;
    this.results = [];

    for (const document of this.documents) {
      try {
        const response = await applyRulesToDocument(
          document.id,
          false,
          this.selectedRule.id,
        );
        this.results = [...this.results, { document, response, error: null }];
      } catch (err: unknown) {
        this.results = [...this.results, {
          document,
          response: null,
          error: err instanceof Error
            ? err.message
            : this.i18n.tr('documents.applyRule.failed'),
        }];
      } finally {
        this.processed++;
      }
    }

    this.running = false;
    this.finished = true;
    this.completed?.(this.changedCount > 0);
  }

  public close(): void {
    if (this.running) return;
    this.closed();
  }
}
